import React, { ReactNode, LegacyRef, CSSProperties, Children } from 'react';
import classNames from 'classnames';
import { useTimelineHeadersContext } from './HeadersContext';
import SidebarHeader from './SidebarHeader';
import { RIGHT_VARIANT } from './constants';
import type { ElementWithSecret } from '../types/main';

export interface TimelineHeadersProps {
  registerScroll: LegacyRef<HTMLDivElement>;
  leftSidebarWidth: number;
  rightSidebarWidth: number;
  style?: React.CSSProperties;
  children?: ReactNode;
  className?: string;
  calendarHeaderStyle?: React.CSSProperties;
  calendarHeaderClassName?: string;
}

const SIDEBAR_HEADER_SECRET_KEY = 'SidebarHeader';
function isSidebarHeader(child: ElementWithSecret): boolean {
  if (child?.type === undefined) return false;
  return (child.type as { secretKey?: string }).secretKey === SIDEBAR_HEADER_SECRET_KEY;
}

function TimelineHeaders({
  registerScroll,
  leftSidebarWidth,
  rightSidebarWidth,
  style,
  children: rawChildren,
  className,
  calendarHeaderStyle,
  calendarHeaderClassName,
}: TimelineHeadersProps) {
  const getRootStyle = (): React.CSSProperties => ({
    ...style,
    display: 'flex',
    width: '100%',
  });

  const getCalendarHeaderStyle = (): React.CSSProperties => ({
    ...calendarHeaderStyle,
    overflow: 'hidden',
    width: `calc(100% - ${leftSidebarWidth + rightSidebarWidth}px)`,
  });

  const childrenList: ElementWithSecret[] = Array.isArray(rawChildren)
    ? (rawChildren.filter(Boolean) as ElementWithSecret[])
    : rawChildren
      ? [rawChildren as ElementWithSecret]
      : [];

  let rightSidebarHeader: React.ReactNode = null;
  let leftSidebarHeader: React.ReactNode = null;
  const calendarHeaders: ElementWithSecret[] = [];

  Children.map(childrenList, (child) => {
    if (isSidebarHeader(child)) {
      if (child?.props?.variant === RIGHT_VARIANT) {
        rightSidebarHeader = child;
      } else {
        leftSidebarHeader = child;
      }
    } else {
      calendarHeaders.push(child);
    }
  });

  if (!leftSidebarHeader) {
    leftSidebarHeader = <SidebarHeader />;
  }
  if (!rightSidebarHeader && rightSidebarWidth) {
    rightSidebarHeader = <SidebarHeader variant="right" />;
  }

  return (
    <div
      data-testid="headerRootDiv"
      style={getRootStyle()}
      className={classNames('rct-header-root', className)}
    >
      {leftSidebarHeader}
      <div
        ref={registerScroll}
        style={getCalendarHeaderStyle()}
        className={classNames('rct-calendar-header', calendarHeaderClassName)}
        data-testid="headerContainer"
      >
        {calendarHeaders}
      </div>
      {rightSidebarHeader}
    </div>
  );
}

TimelineHeaders.secretKey = 'TimelineHeaders';

export interface TimelineHeadersWrapperProps {
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  calendarHeaderStyle?: CSSProperties;
  calendarHeaderClassName?: string;
}

function TimelineHeadersWrapper({
  children,
  style,
  className,
  calendarHeaderStyle,
  calendarHeaderClassName,
}: TimelineHeadersWrapperProps) {
  const { leftSidebarWidth, rightSidebarWidth, registerScroll } = useTimelineHeadersContext();
  return (
    <TimelineHeaders
      leftSidebarWidth={leftSidebarWidth}
      rightSidebarWidth={rightSidebarWidth}
      registerScroll={registerScroll}
      style={style}
      className={className}
      calendarHeaderStyle={calendarHeaderStyle}
      calendarHeaderClassName={calendarHeaderClassName}
    >
      {children}
    </TimelineHeaders>
  );
}

export const TIMELINE_HEADERS_SECRET_KEY = 'TimelineHeaders';
(TimelineHeadersWrapper as { secretKey?: string }).secretKey = TIMELINE_HEADERS_SECRET_KEY;

export default TimelineHeadersWrapper;
